import "leaflet/dist/leaflet.css";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { toast } from "sonner";
import L from "leaflet";
import { Check, ChevronDown, RotateCw, Search } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  MapContainer,
  Marker,
  Popup,
  TileLayer,
  ZoomControl,
  useMap,
  useMapEvents,
} from "react-leaflet";

import { controlClass, controlHeight } from "@/components/ui/field";
import { Page, PageHeader } from "@/components/ui/page";
import { Button } from "@/components/ui/button";
import { LoadingNote } from "@/components/ui/loading";
import { useFetchingKeys } from "@/components/ui/refresh-button";
import { api, errorMessage } from "@/services/api";
import { useDelayed } from "@/hooks/use-delayed";
import type { TranslationKey } from "@/constants/i18n";
import { cn } from "@/utils/utils";
import { useTranslation } from "@/store/locale";

interface PlacePoint {
  id: number;
  name: string;
  population: number | null;
  latitude: number;
  longitude: number;
}
/**
 * A club on the map. Which number it carries depends on which feed answered:
 * the population view asks for its city's population, the member view for its
 * headcount, and they are separate endpoints so they can be cached for
 * different lengths of time.
 */
interface ClubPoint {
  id: number;
  name: string;
  latitude: number;
  longitude: number;
  city_id: number;
  city_name: string;
  city_population?: number | null;
  members?: number;
}
interface MemberPoint {
  id: number;
  name: string;
  latitude: number;
  longitude: number;
  members: number;
}
type Mode = "club-population" | "population" | "members";
type Level = "province" | "city" | "barangay";
type Bounds = { south: number; west: number; north: number; east: number };

const MODES: { key: Mode; label: TranslationKey }[] = [
  { key: "club-population", label: "map.populationByClub" },
  { key: "population", label: "map.philippinesPopulation" },
  { key: "members", label: "map.members" },
];
const PHILIPPINES: [number, number] = [12.6, 122.0];
/**
 * How long a wait has to run before the loader offers a way out of it.
 *
 * Long enough that an ordinary slow answer is never interrupted, short enough
 * that nobody sits looking at a map they cannot move wondering whether it has
 * hung.
 */
const STOP_OFFERED_AFTER_MS = 20_000;

/**
 * What this screen reads. Only the feeds the current view actually uses are in
 * flight, so invalidating all three re-reads the map in front of you and
 * nothing else — the page's own data, not the whole app's.
 */
const MAP_KEYS = [
  ["/maps/clubs/population"],
  ["/maps/clubs/headcount"],
  ["/maps/members"],
  ["/maps/population"],
] as const;

/**
 * What every map feed answers with: the points, when the server read them, and
 * how long that answer has left.
 *
 * The window itself stays on the server. This screen never holds a copy of it —
 * it holds each answer for exactly as long as that answer says it is good, so
 * the backend can lengthen or shorten a TTL and the browser follows without a
 * deploy.
 */
interface Cached<T> {
  cached_at: string;
  expires_in: number;
  points: T[];
}

/**
 * How long the map has to hold still before its view counts as the view.
 *
 * Long enough to swallow a walk down through the levels — a wheel-zoom from
 * provinces to barangays fires `zoomend` at every stop in between — and short
 * enough that letting go of the map feels like it answered immediately.
 */
const SETTLE_MS = 400;

/** Zoomed out, provinces; closer, cities; closer still, the barangays on screen. */
//: How far out from a club still counts as its area, for the barangay drill-down.
const CLUB_AREA_KM = 25;

const levelFor = (zoom: number): Level =>
  zoom <= 7 ? "province" : zoom <= 10 ? "city" : "barangay";
const compact = (value: number) =>
  value >= 1_000_000
    ? `${(value / 1_000_000).toFixed(1)}M`
    : value >= 1_000
      ? `${(value / 1_000).toFixed(1)}k`
      : String(value);

/** A dot, its name above, its number in a pill — all HTML, so the theme's tokens apply. */
function pin(name: string, value: string): L.DivIcon {
  const safe = (text: string) => text.replace(/[&<>"]/g, (c) => `&#${c.charCodeAt(0)};`);
  return L.divIcon({
    className: "",
    iconAnchor: [0, 0],
    html: `<div class="pointer-events-none -translate-x-1/2 -translate-y-full flex flex-col items-center gap-0.5 whitespace-nowrap">
      <span class="text-brand-700 dark:text-brand-200 text-footnote font-semibold drop-shadow-[0_0_2px_var(--surface)]">${safe(name)}</span>
      <span class="bg-accent text-accent-fg rounded-md px-1.5 py-0.5 text-footnote font-semibold shadow">${safe(value)}</span>
      <span class="bg-accent size-2 rounded-full ring-2 ring-white translate-y-1"></span>
    </div>`,
  });
}

/**
 * Holds the map still while its points are loading.
 *
 * Panning and zooming are not innocent here: the zoom and the visible box are
 * both in the query key, so every nudge fires another read and supersedes the
 * one in flight. On a slow answer that is a queue of requests nobody will see.
 * They are this screen's Add and Edit, and they wait the same way — which is
 * also why the `+`/`-` control greys out rather than silently doing nothing.
 */
function ViewLock({ busy }: { busy: boolean }) {
  const map = useMap();
  useEffect(() => {
    const handlers = [
      map.dragging,
      map.scrollWheelZoom,
      map.doubleClickZoom,
      map.touchZoom,
      map.boxZoom,
      map.keyboard,
    ];
    for (const handler of handlers) {
      if (busy) handler?.disable();
      else handler?.enable();
    }
    // Leaflet builds the zoom buttons itself, so they are reached through the
    // DOM rather than given a prop.
    const zoom = map.getContainer().querySelector<HTMLElement>(".leaflet-control-zoom");
    zoom?.classList.toggle("pointer-events-none", busy);
    zoom?.classList.toggle("opacity-50", busy);
  }, [busy, map]);
  return null;
}

/** Reports the zoom and the visible box whenever the map settles. */
function ViewTracker({ onChange }: { onChange: (zoom: number, bounds: Bounds) => void }) {
  // Kept in a ref so the debounce always calls the current handler without the
  // timer restarting every time this re-renders. Written in an effect, not
  // during render.
  const latest = useRef(onChange);
  useEffect(() => {
    latest.current = onChange;
  });
  // Reported when the map settles, not at every step on the way.
  //
  // `zoomend` fires once per level, so scrolling from provinces down to
  // barangays announces the city level in passing — a level nobody asked to
  // see, whose answer is a query over the whole box and is thrown away a
  // moment later. Waiting for the map to stop turns that walk into one read.
  const settle = useRef<ReturnType<typeof setTimeout>>(undefined);
  const report = (map: L.Map) => {
    clearTimeout(settle.current);
    settle.current = setTimeout(() => {
      const b = map.getBounds();
      latest.current(map.getZoom(), {
        south: b.getSouth(),
        west: b.getWest(),
        north: b.getNorth(),
        east: b.getEast(),
      });
    }, SETTLE_MS);
  };
  const map = useMapEvents({ moveend: () => report(map), zoomend: () => report(map) });
  // The first read is the one nobody is waiting through, so it goes at once.
  useEffect(() => {
    const b = map.getBounds();
    latest.current(map.getZoom(), {
      south: b.getSouth(),
      west: b.getWest(),
      north: b.getNorth(),
      east: b.getEast(),
    });
    return () => clearTimeout(settle.current);
  }, [map]);
  return null;
}

function MapPage() {
  const t = useTranslation();
  const [mode, setMode] = useState<Mode>("population");
  const [zoom, setZoom] = useState(6);
  const [bounds, setBounds] = useState<Bounds | null>(null);
  const [selected, setSelected] = useState<Set<number> | "all">("all");

  // Two endpoints, not one with a flag: the member view's headcount is stale
  // in minutes and a city's population is not, so they are cached apart. Which
  // one this screen reads is the only thing the mode decides here.
  const clubsPath = mode === "members" ? "/maps/clubs/headcount" : "/maps/clubs/population";
  const clubs = useQuery({
    queryKey: [clubsPath],
    queryFn: async ({ signal }) =>
      (await api.get<Cached<ClubPoint>>(clubsPath, { signal })).data,
    // Held for what the server says is left of this answer's window, so a
    // TTL change on the backend needs no deploy here.
    staleTime: (query) => (query.state.data?.expires_in ?? 0) * 1000,
  });

  // Round the box so a nudge of the map does not refetch; pad it so the edges are covered.
  const level = levelFor(zoom);
  const box = bounds
    ? {
        south: Math.floor((bounds.south - 0.2) * 10) / 10,
        west: Math.floor((bounds.west - 0.2) * 10) / 10,
        north: Math.ceil((bounds.north + 0.2) * 10) / 10,
        east: Math.ceil((bounds.east + 0.2) * 10) / 10,
      }
    : null;
  const shownClubs = useMemo(
    () =>
      (clubs.data?.points ?? []).filter((club) => selected === "all" || selected.has(club.id)),
    [clubs.data?.points, selected],
  );
  // Both club views show clubs until zoomed past the city level, then drill:
  // the population view into the barangays of the selected clubs' cities, the
  // member view into the barangays where those clubs' members live. The
  // Philippines view walks province, city, barangay by zoom.
  const drilling = mode !== "population" && zoom >= 11;
  const placeLevel: Level | null =
    mode === "population" ? level : mode === "club-population" && drilling ? "barangay" : null;
  const cityIds =
    mode === "club-population" && drilling
      ? [...new Set(shownClubs.map((club) => club.city_id))].sort()
      : null;
  const clubIds = shownClubs.map((club) => club.id).sort();
  const memberDrill = mode === "members" && drilling;
  const memberPoints = useQuery({
    queryKey: ["/maps/members", clubIds, box],
    queryFn: async ({ signal }) =>
      (
        await api.get<Cached<MemberPoint>>("/maps/members", {
          // The barangays a club draws from, measured out from the club itself
          // rather than from whatever the viewport happens to frame.
          params: { club_id: clubIds, radius_km: CLUB_AREA_KM, ...box },
          signal,
        })
      ).data,
    enabled: memberDrill && box !== null && clubIds.length > 0,
    // Held for what the server says is left of this answer's window, so a
    // TTL change on the backend needs no deploy here.
    staleTime: (query) => (query.state.data?.expires_in ?? 0) * 1000,
    placeholderData: (previous) => previous,
  });
  const places = useQuery({
    queryKey: ["/maps/population", placeLevel, placeLevel === "province" ? null : box, cityIds],
    queryFn: async ({ signal }) =>
      (
        await api.get<Cached<PlacePoint>>("/maps/population", {
          params: {
            level: placeLevel,
            ...(placeLevel === "province" ? {} : box),
            ...(cityIds ? { city_id: cityIds } : {}),
          },
          signal,
        })
      ).data,
    enabled:
      placeLevel !== null &&
      (placeLevel === "province" || box !== null) &&
      (cityIds === null || cityIds.length > 0),
    // Held for what the server says is left of this answer's window, so a
    // TTL change on the backend needs no deploy here.
    staleTime: (query) => (query.state.data?.expires_in ?? 0) * 1000,
    placeholderData: (previous) => previous,
  });

  // The feed the badge is describing — the one whose age is worth showing.
  const showing = memberDrill ? memberPoints : placeLevel !== null ? places : clubs;
  const readAt = showing.data ? Date.parse(showing.data.cached_at) : 0;
  // The server sends what is *left* of the window, not the window, so the
  // moment it expires is that plus when the answer arrived. Worked out here
  // rather than sent whole, because the backend can then change a TTL without
  // this screen ever knowing what the old one was.
  const expiresAt = showing.data ? showing.dataUpdatedAt + showing.data.expires_in * 1000 : 0;

  // What is on screen right now, summed: the number the badge shows.
  const inView = (lat: number, lng: number) =>
    bounds === null ||
    (lat >= bounds.south && lat <= bounds.north && lng >= bounds.west && lng <= bounds.east);
  const visible = memberDrill
    ? (memberPoints.data?.points ?? []).filter((p) => inView(p.latitude, p.longitude))
    : placeLevel !== null
      ? (places.data?.points ?? []).filter((p) => inView(p.latitude, p.longitude))
      : shownClubs.filter((c) => inView(c.latitude, c.longitude));
  const valueOf = (point: PlacePoint | ClubPoint | MemberPoint): number =>
    "population" in point
      ? (point.population ?? 0)
      : // A club carries whichever number the feed it came from supplies, so
        // there is nothing to choose between here — one of the two is set.
        "city_name" in point
        ? (point.members ?? point.city_population ?? 0)
        : point.members;
  const total = visible.reduce((sum, point) => sum + valueOf(point), 0);

  // The tiles come from OpenStreetMap and are never the wait; the points are.
  const busy = memberPoints.isFetching || places.isFetching || clubs.isFetching;
  // Delayed for the note, immediate for the controls: a loader that flashes is
  // a glitch, but a control that stays live for 300ms is a queued request.
  const plotting = useDelayed(busy);
  // Past its window, so worth asking again. `isStale` is react-query's answer
  // against the window the server sent, so this arrives on its own — no clock
  // of this screen's making, and no copy of a TTL it does not own.
  const cached = readAt > 0 && showing.isStale && !busy;
  // A wait this long has stopped being progress and become a question, so it
  // gets an answer: a way out. Below it, offering one would only invite people
  // to cancel reads that were about to land anyway.
  const stuck = useDelayed(busy, { after: STOP_OFFERED_AFTER_MS, atLeast: 0 });
  const [stopped, setStopped] = useState(false);
  // Any new read clears the notice, adjusted during render rather than in an
  // effect: an effect would paint the stale notice once and then correct it.
  const [reading, setReading] = useState(busy);
  if (busy !== reading) {
    setReading(busy);
    if (busy) setStopped(false);
  }
  const queryClient = useQueryClient();
  const stop = () => {
    setStopped(true);
    for (const queryKey of MAP_KEYS) void queryClient.cancelQueries({ queryKey });
  };

  return (
    <Page>
      <PageHeader title={t("nav.memberPopulation")} description={t("map.subtitle")} />

      {/* Above the map, not floating on it: these three change what is asked
          for, and an input that sits on top of the answer covers the densest
          part of it — the club list opened a 400px sheet over Luzon. What
          stays on the map is what describes the view it is drawn on: the
          running total, the zoom, and the note saying why neither will move. */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <fieldset
          disabled={busy}
          role="radiogroup"
          aria-label={t("map.view")}
          className="bg-surface-raised border-line flex flex-wrap items-center gap-1 rounded-full border p-1"
        >
          {MODES.map(({ key, label }) => (
            <button
              key={key}
              type="button"
              role="radio"
              aria-checked={mode === key}
              onClick={() => setMode(key)}
              className={cn(
                "text-callout flex items-center gap-2 rounded-full px-3 py-1.5 transition-colors",
                "disabled:pointer-events-none disabled:opacity-50",
                mode === key
                  ? "bg-accent-soft text-accent font-medium"
                  : "text-fg hover:bg-surface-inset",
              )}
            >
              <span
                aria-hidden
                className={cn(
                  "grid size-4 place-items-center rounded-full border",
                  mode === key ? "border-accent" : "border-line-strong",
                )}
              >
                {mode === key ? <span className="bg-accent size-2 rounded-full" /> : null}
              </span>
              {t(label)}
            </button>
          ))}
        </fieldset>
        {/* Places, not clubs, in the population view: the club filter has nothing to narrow. */}
        {mode !== "population" ? (
          <fieldset disabled={busy}>
            <ClubFilter
              clubs={clubs.data?.points ?? []}
              selected={selected}
              onChange={setSelected}
            />
          </fieldset>
        ) : null}
        <fieldset disabled={busy} className="ml-auto">
          <MapRefresh />
        </fieldset>
      </div>

      <div className="rounded-card relative overflow-hidden shadow-[var(--shadow-lift)]">
        {plotting || stopped ? (
          // Centred and over the map rather than instead of it: the tiles are
          // already useful while the points arrive, so this floats above a map
          // somebody can still read. It sits in the middle because it is not
          // only a loader — it is also the answer to "why will this not zoom",
          // and that answer has to be where the eye goes.
          <div className="pointer-events-none absolute inset-0 z-[1002] grid place-items-center">
            <div className="animate-note bg-surface-overlay border-line pointer-events-auto grid max-w-[min(22rem,calc(100%-2rem))] gap-2 rounded-2xl border px-4 py-3.5 text-center shadow-[var(--shadow-float)] backdrop-blur-xl">
              {stopped ? (
                <p className="text-fg text-callout">{t("map.stopped")}</p>
              ) : (
                <>
                  <LoadingNote
                    action="loading"
                    subject={t("map.points")}
                    className="justify-center"
                  />
                  {/* Why the map will not move, said where the wait is shown —
                      a control that does nothing and does not say why reads as
                      broken. */}
                  <p className="text-fg-muted text-footnote text-pretty">{t("map.held")}</p>
                  {stuck ? (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={stop}
                      className="justify-self-center"
                    >
                      {t("map.stop")}
                    </Button>
                  ) : null}
                </>
              )}
            </div>
          </div>
        ) : null}
        <MapContainer
          center={PHILIPPINES}
          zoom={6}
          minZoom={5}
          scrollWheelZoom
          // Placed by hand below, because Leaflet's own corner is taken.
          zoomControl={false}
          className="bg-surface-inset h-[calc(100dvh-16.5rem)] min-h-[28rem] w-full"
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <ViewTracker
            onChange={(nextZoom, nextBounds) => {
              setZoom(nextZoom);
              setBounds(nextBounds);
            }}
          />
          <ViewLock busy={busy} />
          {/* Bottom right, the one corner nothing else claims: the club filter
              holds the top left — where Leaflet puts these by default, so `+`
              opened the club list instead of zooming — the view switch and
              Refresh hold the top right, and the running total the bottom
              left. It stacks above the attribution rather than over it. */}
          <ZoomControl position="bottomright" />
          {memberDrill
            ? (memberPoints.data?.points ?? []).map((point) => (
                <Marker
                  key={`members-${point.id}`}
                  position={[point.latitude, point.longitude]}
                  icon={pin(point.name, compact(point.members))}
                >
                  <Popup>
                    <strong>{point.name}</strong>
                    <br />
                    {`${point.members.toLocaleString()} ${t("map.membersUnit")}`}
                  </Popup>
                </Marker>
              ))
            : placeLevel !== null
              ? (places.data?.points ?? []).map((place) => (
                  <Marker
                    key={`${placeLevel}-${place.id}`}
                    position={[place.latitude, place.longitude]}
                    icon={pin(
                      place.name,
                      place.population === null ? "—" : compact(place.population),
                    )}
                  >
                    <Popup>
                      <strong>{place.name}</strong>
                      <br />
                      {place.population === null
                        ? t("map.noPopulation")
                        : `${place.population.toLocaleString()} ${t("map.population")}`}
                    </Popup>
                  </Marker>
                ))
              : shownClubs.map((club) => {
                  // Null only means "not recorded", which the pin shows as a
                  // dash; a missing field means the other feed answered.
                  const value =
                    mode === "members" ? (club.members ?? 0) : (club.city_population ?? null);
                  return (
                    <Marker
                      key={`club-${club.id}`}
                      position={[club.latitude, club.longitude]}
                      icon={pin(club.name, value === null ? "—" : compact(value))}
                    >
                      <Popup>
                        <strong>{club.name}</strong>
                        <br />
                        {club.city_name}
                        <br />
                        {mode === "members"
                          ? `${(club.members ?? 0).toLocaleString()} ${t("map.membersUnit")}`
                          : club.city_population == null
                            ? t("map.noPopulation")
                            : `${club.city_population.toLocaleString()} ${t("map.population")}`}
                      </Popup>
                    </Marker>
                  );
                })}
        </MapContainer>

        <div className="bg-surface-overlay border-line absolute bottom-4 left-4 z-[1001] rounded-xl border px-3.5 py-2 shadow-[var(--shadow-float)] backdrop-blur-xl">
          <p className="text-fg text-callout font-semibold tabular-nums">
            {compact(total)}{" "}
            <span className="text-fg-muted font-normal">
              {mode === "members" ? t("map.membersUnit") : t("map.population")} ·{" "}
              {visible.length}{" "}
              {memberDrill
                ? t("map.unit.barangay")
                : placeLevel === null
                  ? t("map.unit.club")
                  : t(`map.unit.${placeLevel}`)}{" "}
              {t("map.inView")}
            </span>
          </p>
          <p className="text-fg-muted text-footnote mt-0.5">
            {mode === "members"
              ? memberDrill
                ? t("map.level.memberBarangays")
                : t("map.level.memberClubs")
              : placeLevel === null
                ? t("map.level.clubs")
                : t(`map.level.${placeLevel}`)}
            {places.isFetching || memberPoints.isFetching ? " …" : ""}
          </p>
          {/* When the server read what is on screen — not when this browser
              fetched it. With an answer that can be half an hour old those are
              different numbers, and only one of them is the age of the data. */}
          {readAt > 0 ? (
            <p className="text-fg-subtle text-footnote mt-0.5">
              {t("map.cacheDate").replace("{time}", format(new Date(readAt), "h:mm a"))}
              {cached
                ? ` · ${t("map.cached")}`
                : ` · ${t("map.expiresAt").replace("{time}", format(new Date(expiresAt), "h:mm a"))}`}
            </p>
          ) : null}
        </div>
      </div>
    </Page>
  );
}

/**
 * Refresh, for a screen whose cache is not in this browser.
 *
 * The generic `RefreshButton` invalidates react-query, which here would only
 * ask the API again and be handed the same remembered answer. So this drops the
 * server's copy first and *then* invalidates — awaited in that order, because a
 * refetch that started first would repopulate the very keys the clear is about
 * to delete.
 */
function MapRefresh() {
  const t = useTranslation();
  const queryClient = useQueryClient();
  const fetching = useFetchingKeys(MAP_KEYS);
  const clear = useMutation({
    mutationFn: async () => {
      await api.delete("/maps/cache");
    },
    onSuccess: () => {
      for (const queryKey of MAP_KEYS) void queryClient.invalidateQueries({ queryKey });
    },
    onError: (error) => toast.error(errorMessage(error, t("map.refreshFailed"))),
  });
  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={() => clear.mutate()}
      loading={clear.isPending || fetching}
      loadingLabel={t("rp.refresh")}
    >
      <RotateCw aria-hidden />
      {t("rp.refresh")}
    </Button>
  );
}

/** Which clubs the club views plot: all by default, or a ticked subset, with a search box. */
function ClubFilter({
  clubs,
  selected,
  onChange,
}: {
  clubs: ClubPoint[];
  selected: Set<number> | "all";
  onChange: (next: Set<number> | "all") => void;
}) {
  const t = useTranslation();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const root = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const away = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", away);
    return () => document.removeEventListener("pointerdown", away);
  }, [open]);

  const matches = clubs.filter((club) =>
    club.name.toLowerCase().includes(search.toLowerCase()),
  );
  const isOn = (id: number) => selected === "all" || selected.has(id);
  const toggle = (id: number) => {
    const next = new Set(selected === "all" ? clubs.map((club) => club.id) : selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange(next.size === clubs.length ? "all" : next);
  };
  const count = selected === "all" ? clubs.length : selected.size;

  return (
    <div ref={root} className="relative w-72 max-w-[calc(100vw-3rem)]">
      <button
        type="button"
        onClick={() => setOpen((was) => !was)}
        aria-expanded={open}
        className={cn(
          controlClass,
          controlHeight.md,
          "bg-surface-raised flex items-center gap-2 text-left",
          "disabled:pointer-events-none disabled:opacity-50",
        )}
      >
        <span className="min-w-0 flex-1 truncate">
          {selected === "all"
            ? t("map.allClubs")
            : t("map.clubsSelected").replace("{count}", String(count))}
        </span>
        <ChevronDown className="text-fg-muted size-4 shrink-0" aria-hidden />
      </button>
      {open ? (
        // Absolute now that this lives in a toolbar rather than floating over
        // the map: in flow it would push the map down every time it opened.
        // Above 1002 because Leaflet's control corners are z-index 1000 and
        // `.leaflet-container` has no z-index of its own — so it is not a
        // stacking context and those numbers compete with this one directly —
        // and because the loader card over the map sits at 1002.
        <div className="bg-surface-raised border-line animate-sheet absolute top-full left-0 z-[1003] mt-2 w-full rounded-xl border p-2 shadow-[var(--shadow-float)]">
          <label className="relative block">
            <Search
              className="text-fg-subtle pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2"
              aria-hidden
            />
            <input
              type="search"
              autoFocus
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={t("rp.search")}
              className="border-line bg-surface text-fg placeholder:text-fg-subtle focus-visible:border-accent text-callout h-10 w-full rounded-lg border pr-3 pl-9 outline-none"
            />
          </label>
          <ul className="mt-2 max-h-72 overflow-y-auto">
            <li>
              <label className="hover:bg-surface-inset text-callout flex cursor-pointer items-center gap-3 rounded-md px-2 py-2">
                <span
                  className={cn(
                    "grid size-4.5 place-items-center rounded border",
                    selected === "all"
                      ? "bg-accent border-accent text-accent-fg"
                      : "border-line-strong",
                  )}
                >
                  {selected === "all" ? <Check className="size-3" strokeWidth={3} /> : null}
                </span>
                <input
                  type="checkbox"
                  className="sr-only"
                  checked={selected === "all"}
                  onChange={(event) => onChange(event.target.checked ? "all" : new Set())}
                />
                {t("map.all")}
              </label>
            </li>
            {matches.map((club) => (
              <li key={club.id}>
                <label className="hover:bg-surface-inset text-callout flex cursor-pointer items-center gap-3 rounded-md px-2 py-2">
                  <span
                    className={cn(
                      "grid size-4.5 place-items-center rounded border",
                      isOn(club.id)
                        ? "bg-accent border-accent text-accent-fg"
                        : "border-line-strong",
                    )}
                  >
                    {isOn(club.id) ? <Check className="size-3" strokeWidth={3} /> : null}
                  </span>
                  <input
                    type="checkbox"
                    className="sr-only"
                    checked={isOn(club.id)}
                    onChange={() => toggle(club.id)}
                  />
                  <span className="truncate">{club.name}</span>
                </label>
              </li>
            ))}
            {matches.length === 0 ? (
              <li className="text-fg-muted text-callout px-2 py-3">{t("common.nothingYet")}</li>
            ) : null}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

export default MapPage;
